import React, { useState } from 'react';
import {
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { IItem } from './interfaces';
import { useDraggable } from '@dnd-kit/core';
import { AgentBadge } from '../../../../../components/AgentBadge';
import { KeyValue } from './components/KeyValue';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from '../../../../../context/AuthProvider';

export const Item: React.FC<IItem> = ({
  id,
  title,
  product,
  client,
  contact,
  agent,
  canCloseOpportunity,
  onCloseOpportunity,
  onDelete,
  onEdit,
}): JSX.Element => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
    setActivatorNodeRef,
  } = useDraggable({
    id,
  });
  const { auth } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;
  const { t } = useTranslation();

  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        backgroundColor: '#FFF',
        padding: '10px',
        borderRadius: '4px',
        '&:hover .opportunity-menu-btn': {
          opacity: 1,
        },
        opacity: isDragging ? 0 : 1,
        ...style,
      }}
    >
      <Stack ref={setActivatorNodeRef} {...listeners} {...attributes}>
        <Typography
          fontWeight="bold"
          color="#222f3e"
          fontSize="1.1rem"
          sx={{
            mb: '10px',
          }}
        >
          {title}
        </Typography>

        <Stack gap="10px">
          <KeyValue label="Client" value={client} />
          <KeyValue label="Product" value={product} />
          <KeyValue label="Contact person" value={contact} />
        </Stack>

        <Stack
          direction="row"
          alignItems="end"
          sx={{
            mt: '10px',
            marginLeft: 'auto',
          }}
        >
          <AgentBadge agent={agent} size="small" />
        </Stack>
      </Stack>

      <IconButton
        aria-label="item-menu"
        sx={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          opacity: 0,
          transition: 'opacity 100ms ease-in',
        }}
        className="opportunity-menu-btn"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          key="edit"
          dense
          sx={{
            '& .MuiListItemIcon-root': {
              minWidth: 25,
            },
          }}
          onClick={() => {
            if (onEdit) {
              onEdit();
            }

            closeMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography fontSize="14px" variant="inherit">
            {t('buttons.edit')}
          </Typography>
        </MenuItem>

        <MenuItem
          key="delete"
          dense
          sx={{
            '& .MuiListItemIcon-root': {
              minWidth: 25,
            },
          }}
          onClick={() => {
            if (onDelete) {
              onDelete();
            }

            closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteIcon color="error" fontSize="small" />
          </ListItemIcon>
          <Typography fontSize="14px" variant="inherit">
            {t('buttons.delete')}
          </Typography>
        </MenuItem>

        {canCloseOpportunity && (
          <MenuItem
            key="close"
            dense
            sx={{
              '& .MuiListItemIcon-root': {
                minWidth: 25,
              },
            }}
            onClick={() => {
              if (onCloseOpportunity) {
                onCloseOpportunity();
              }

              closeMenu();
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            </ListItemIcon>
            <Typography fontSize="14px" variant="inherit">
              {t('opportunities:dashboard.buttons.closeOpportunity')}
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
